import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import '../../../assets/styles/buyer_goods_receipt_generate.scss';
import LinkedLogisticsPanel from '../../../components/moduleLogistics/LinkedLogisticsPanel';

/** Logistics RFQ / provider manifest: hide PO + invoice fields in GRN form and preview. */
function isLogisticsFlowManifest(m) {
  if (!m || typeof m !== 'object') return false;
  const lq = m.logistics_quote_id ?? m.logisticsQuoteId;
  const lg = m.logistics_generic_rfq_id ?? m.logisticsGenericRfqId;
  const lqNum = lq != null && String(lq).trim() !== '' ? Number(lq) : NaN;
  const lgNum = lg != null && String(lg).trim() !== '' ? Number(lg) : NaN;
  return (Number.isFinite(lqNum) && lqNum > 0) || (Number.isFinite(lgNum) && lgNum > 0);
}

/** Matches first option in the Warehouse Location dropdown (logistics / manifest-only UX). */
const GRN_DEFAULT_WAREHOUSE = 'Main Warehouse - Bangalore';

function getDefaultGrnWarehouseLocation(isLogisticsGrnFlow, manifestFull, manifestPartial) {
  const m = manifestFull || manifestPartial;
  if (isLogisticsGrnFlow) return GRN_DEFAULT_WAREHOUSE;
  if (isLogisticsFlowManifest(m)) return GRN_DEFAULT_WAREHOUSE;
  return '';
}

/** Stable numeric manifest PK for API (handles snake_case / camelCase from different sources). */
function resolveManifestRecordId(manifestFull, manifestPartial) {
  const raw =
    manifestFull?.id ??
    manifestFull?.manifest_id ??
    manifestPartial?.id ??
    manifestPartial?.manifest_id ??
    manifestPartial?.shipping_manifest_id ??
    manifestPartial?.shippingManifestId ??
    null;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function BuyerGoodsReceiptGenerate() {
  const navigate = useNavigate();
  const location = useLocation();

  /** State is cleared on full page refresh; URL keeps logistics manifest context. */
  const manifestData = useMemo(() => {
    const fromState = location.state?.manifest;
    if (fromState) return fromState;
    const sp = new URLSearchParams(location.search);
    const q = sp.get('manifestId');
    if (q == null || String(q).trim() === '') return null;
    const n = Number(q);
    return Number.isFinite(n) && n > 0 ? { id: n } : null;
  }, [location.state?.manifest, location.search]);

  const grnLogisticsFlow = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return Boolean(
      location.state?.grnLogisticsFlow === true ||
        sp.get('logisticsGrn') === '1' ||
        String(sp.get('logisticsGrn') || '').toLowerCase() === 'true'
    );
  }, [location.state?.grnLogisticsFlow, location.search]);

  const acceptanceFormData = location.state?.acceptanceForm;
  const returnToLogisticsSent = grnLogisticsFlow;
  let grnId = location.state?.grnId;
  let editMode = location.state?.editMode || false;

  // Fallback: Try to get GRN ID from manifest data if not in navigation state
  if (!grnId && manifestData?.grn?.id) {
    grnId = manifestData.grn.id;
    editMode = true;
    console.log('Fallback: Found GRN ID from manifest data:', grnId);
  }

  // Fallback: Try to get GRN ID from URL search params
  const searchParams = new URLSearchParams(location.search);
  if (!grnId && searchParams.get('grnId')) {
    grnId = parseInt(searchParams.get('grnId'), 10);
    editMode = true;
    console.log('Fallback: Found GRN ID from URL params:', grnId);
  }
  
  // Debug logging
  console.log('BuyerGoodsReceiptGenerate - Navigation state:', {
    hasManifest: !!manifestData,
    hasAcceptanceForm: !!acceptanceFormData,
    grnId: grnId,
    editMode: editMode,
    manifestGrnId: manifestData?.grn?.id,
    locationState: location.state,
    urlParams: location.search
  });
  
  const [loading, setLoading] = useState(true);
  const [grnData, setGrnData] = useState(null);
  const [poData, setPoData] = useState(null);
  const [manifestFullData, setManifestFullData] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingPOs, setLoadingPOs] = useState(false);

  /** Manifest-linked GRN with no buyer PO row (e.g. logistics RFQ flow): PO id not required. */
  const manifestOnlyGrn = useMemo(() => {
    const m = manifestFullData;
    if (!m || m.id == null) return false;
    const pid = m.purchase_order_id;
    if (pid == null || pid === '') return true;
    const n = Number(pid);
    return !Number.isFinite(n) || n <= 0;
  }, [manifestFullData]);

  /** Hide PO / invoice UI only for logistics-linked manifests (or explicit transit navigation). */
  const hideGrnPoInvoiceFields = useMemo(() => {
    if (grnLogisticsFlow) return true;
    return isLogisticsFlowManifest(manifestFullData);
  }, [grnLogisticsFlow, manifestFullData]);

  /** Allow create/submit without PO when manifest id is known and flow is logistics or manifest has no PO. */
  const allowGrnWithoutPurchaseOrder = useMemo(() => {
    const manifestIdNum = resolveManifestRecordId(manifestFullData, manifestData);
    if (!manifestIdNum) return false;
    if (grnLogisticsFlow) return true;
    return (
      hideGrnPoInvoiceFields ||
      manifestOnlyGrn ||
      isLogisticsFlowManifest(manifestFullData || manifestData)
    );
  }, [manifestFullData, manifestData, grnLogisticsFlow, hideGrnPoInvoiceFields, manifestOnlyGrn]);

  // Generate GRN Number (similar to PO number generation)
  const generateGRNNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    
    // Generate a unique 6-digit number from timestamp
    // Use the last 6 digits of the timestamp (milliseconds since epoch)
    // This ensures uniqueness while keeping the format readable
    const timestamp = now.getTime();
    const sequence = String(timestamp).slice(-6).padStart(6, '0');
    
    return `GRN-${year}-${sequence}`;
  };

  // Fetch data from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // If edit mode, fetch GRN data
        if (editMode && grnId) {
          const grnResponse = await CommonService.get(`/grn/${grnId}`);
          if (grnResponse && grnResponse.success && grnResponse.data) {
            setGrnData(grnResponse.data);
            
            // Fetch PO data if available
            if (grnResponse.data.purchase_order_id) {
              try {
                const poResponse = await CommonService.get(`/purchase-order/${grnResponse.data.purchase_order_id}`);
                if (poResponse && poResponse.success && poResponse.data) {
                  setPoData(poResponse.data);
                }
              } catch (poError) {
                console.warn('Error fetching PO data:', poError);
              }
            }
            
            // Fetch manifest data if available
            if (grnResponse.data.manifest_id) {
              try {
                const manifestResponse = await CommonService.get(`/shipping-manifest/${grnResponse.data.manifest_id}`);
                if (manifestResponse && manifestResponse.success && manifestResponse.data) {
                  setManifestFullData(manifestResponse.data);
                }
              } catch (manifestError) {
                console.warn('Error fetching manifest data:', manifestError);
              }
            }
          } else {
            toast.error('Failed to fetch GRN data');
          }
        } else {
          const manifestFetchId = resolveManifestRecordId(null, manifestData);
          if (manifestFetchId) {
          // If creating new GRN, fetch full manifest data
          try {
            const manifestResponse = await CommonService.get(`/shipping-manifest/${manifestFetchId}`);
            if (manifestResponse && manifestResponse.success && manifestResponse.data) {
              setManifestFullData(manifestResponse.data);
              
              // Fetch PO data if PO number or purchase_order_id is available
              if (manifestResponse.data.purchase_order_id) {
                try {
                  const poResponse = await CommonService.get(`/purchase-order/${manifestResponse.data.purchase_order_id}`);
                  if (poResponse && poResponse.success && poResponse.data) {
                    setPoData(poResponse.data);
                  }
                } catch (poError) {
                  console.warn('Error fetching PO data:', poError);
                }
              } else if (manifestResponse.data.po_number) {
                try {
                  // Try to extract PO ID from PO number format PO-{id}
                  const poNumber = manifestResponse.data.po_number;
                  const poIdMatch = poNumber.match(/PO-(\d+)/);
                  if (poIdMatch) {
                    const poId = parseInt(poIdMatch[1]);
                    const poResponse = await CommonService.get(`/purchase-order/${poId}`);
                    if (poResponse && poResponse.success && poResponse.data) {
                      setPoData(poResponse.data);
                    }
                  }
                } catch (poError) {
                  console.warn('Error fetching PO data:', poError);
                }
              }
            }
          } catch (manifestError) {
            console.error('Error fetching manifest data:', manifestError);
            toast.error('Failed to fetch manifest data');
          }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [editMode, grnId, manifestData]);

  // Fetch only the relevant PO (from manifest or GRN)
  useEffect(() => {
    const fetchRelevantPO = async () => {
      try {
        setLoadingPOs(true);
        let poId = null;
        
        // Get PO ID from GRN data if editing
        if (editMode && grnData && grnData.purchase_order_id) {
          poId = grnData.purchase_order_id;
        }
        // Get PO ID from manifest data if creating
        else if (manifestFullData && manifestFullData.purchase_order_id) {
          poId = manifestFullData.purchase_order_id;
        }
        // Try to extract from PO number if available
        else if (manifestFullData && manifestFullData.po_number) {
          const poNumber = manifestFullData.po_number;
          const poIdMatch = poNumber.match(/PO-(\d+)/);
          if (poIdMatch) {
            poId = parseInt(poIdMatch[1]);
          }
        }
        // Try from manifestData if available
        else if (manifestData && manifestData.poNumber) {
          const poNumber = manifestData.poNumber;
          const poIdMatch = poNumber.match(/PO-(\d+)/);
          if (poIdMatch) {
            poId = parseInt(poIdMatch[1]);
          }
        }
        
        // If we have a PO ID, fetch only that PO
        if (poId) {
          try {
            const response = await CommonService.get(`/purchase-order/${poId}`);
            if (response && response.success && response.data) {
              setPurchaseOrders([response.data]);
            }
          } catch (error) {
            console.error('Error fetching PO:', error);
            setPurchaseOrders([]);
          }
        } else {
          // If no PO ID found, set empty array
          setPurchaseOrders([]);
        }
      } catch (error) {
        console.error('Error fetching purchase order:', error);
        setPurchaseOrders([]);
      } finally {
        setLoadingPOs(false);
      }
    };

    // Only fetch after initial data is loaded
    if (!loading) {
      fetchRelevantPO();
    }
  }, [loading, editMode, grnData, manifestFullData, manifestData]);

  // Initialize form data based on fetched data
  const getInitialFormData = () => {
    // If editing and GRN data is available, use GRN data
    if (editMode && grnData) {
      const qualityChecklist = (grnData.quality_inspection_checklist && typeof grnData.quality_inspection_checklist === 'object') 
        ? grnData.quality_inspection_checklist 
        : (typeof grnData.quality_inspection_checklist === 'string' 
          ? JSON.parse(grnData.quality_inspection_checklist) 
          : {});
      
      const poId = (poData && poData.id) ? poData.id : (grnData.purchase_order_id || null);
      const selectedPO = poId ? `PO-${poId}` : '';
      
      return {
        grnNumber: grnData.grn_number || generateGRNNumber(),
        grnDate: new Date().toISOString().split('T')[0], // Always use today's date
        selectedPO: selectedPO,
        warehouseLocation:
          grnData.warehouse_location ||
          getDefaultGrnWarehouseLocation(grnLogisticsFlow, manifestFullData, manifestData),
        
        // Supplier/Vendor Details (From PO or GRN)
        supplierName: grnData.supplier_name || (poData ? poData.seller_company_name : '') || '',
        supplierGSTIN: grnData.supplier_gstin || (poData ? poData.seller_company_gst_number : '') || '',
        contactPerson: grnData.contact_person || '',
        phone: grnData.phone || '',
        address: grnData.address || (poData ? poData.seller_company_address : '') || '',
        
        // Delivery & Transport Information
        deliveryChallanNo: grnData.delivery_challan_no || '',
        invoiceNumber: grnData.invoice_number || '',
        manifestNumber: grnData.manifest_number || '',
        deliveryDate: grnData.delivery_date ? new Date(grnData.delivery_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        vehicleNumber: grnData.vehicle_number || '',
        driverName: grnData.driver_name || '',
        driverContact: grnData.driver_contact || '',
        lrNumber: grnData.lr_number || '',
        transportCompany: grnData.transport_company || '',
        receivedBy: grnData.received_by_name || '',
        
        // Physical Verification Summary
        packagesExpected: grnData.packages_expected?.toString() || '',
        packagesReceived: grnData.packages_received?.toString() || '',
        weightExpected: grnData.weight_expected_kg?.toString() || '',
        weightReceived: grnData.weight_received_kg?.toString() || '',
        dimensionsVerified: grnData.dimensions_verified === 1,
        packagingIntegrityOK: grnData.packaging_integrity_ok === 1,
        sealsVerified: grnData.seals_verified === 1,
        sealNumbers: grnData.seal_numbers || '',
        
        // Quality Inspection Checklist
        packagingIntact: qualityChecklist.packagingIntact || false,
        correctLabeling: qualityChecklist.correctLabeling || false,
        noVisibleDamage: qualityChecklist.noVisibleDamage || false,
        quantityMatches: qualityChecklist.quantityMatches || false,
        meetsQualityStandards: qualityChecklist.meetsQualityStandards || false,
        allDocumentsComplete: qualityChecklist.allDocumentsComplete || false,
        inspectorName: grnData.inspector_name || '',
        inspectionDate: grnData.inspection_date ? new Date(grnData.inspection_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        defectDescription: grnData.defect_issue_description || '',
        
        // Discrepancies & Issues
        shortageDetected: grnData.shortage_detected === 1,
        shortageDescription: grnData.additional_notes || '', // May contain shortage info
        damageDetected: grnData.damage_detected === 1,
        damageDescription: grnData.additional_notes || '', // May contain damage info
        qualityIssue: grnData.quality_issue === 1,
        qualityIssueDescription: grnData.defect_issue_description || '',
        actionRequired: '',
        
        // Additional Notes
        additionalNotes: grnData.additional_notes || ''
      };
    }
    
    // If creating new GRN, use manifest and acceptance data
    const manifest = manifestFullData || manifestData;
    const actualArrivalDate = manifest?.actual_arrival_date || manifest?.accepted_date || manifest?.actualArrival;
    const deliveryDate = acceptanceFormData?.dateReceived || actualArrivalDate;
    
    const poId = (poData && poData.id) ? poData.id : null;
    const selectedPO = manifest?.po_number || (poId ? `PO-${poId}` : '');
    
    return {
      grnNumber: generateGRNNumber(),
      grnDate: new Date().toISOString().split('T')[0], // Always use today's date
      selectedPO: selectedPO,
      warehouseLocation: getDefaultGrnWarehouseLocation(grnLogisticsFlow, manifestFullData, manifestData),
      
      // Supplier/Vendor Details (From Manifest or PO)
      supplierName: manifest?.from_company_name || poData?.seller_company_name || '',
      supplierGSTIN: manifest?.from_gstin || poData?.seller_company_gst_number || '',
      contactPerson: manifest?.from_contact_person || '',
      phone: manifest?.from_phone || '',
      address: manifest?.from_address_line1 || poData?.seller_company_address || '',
      
      // Delivery & Transport Information
      deliveryChallanNo: (() => {
        // Try to get delivery challan from manifest documents
        if (manifest?.documents && Array.isArray(manifest.documents)) {
          const challanDoc = manifest.documents.find(doc => 
            doc.document_type?.toLowerCase().includes('challan') ||
            doc.document_type?.toLowerCase().includes('delivery')
          );
          if (challanDoc && challanDoc.document_name) {
            // Try to extract challan number from document name
            const challanMatch = challanDoc.document_name.match(/(?:DC|CHALLAN|DC-)([A-Z0-9-]+)/i);
            if (challanMatch) return challanMatch[0];
            return challanDoc.document_name;
          }
        }
        return '';
      })(),
      invoiceNumber: manifest?.invoice_bill_number || '',
      manifestNumber: manifest?.manifest_number || '',
      deliveryDate: deliveryDate ? new Date(deliveryDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      vehicleNumber: manifest?.vehicle_number || '',
      driverName: manifest?.driver_name || '',
      driverContact: manifest?.driver_contact_number || '',
      lrNumber: manifest?.lr_number || '',
      transportCompany: '',
      receivedBy: acceptanceFormData?.receivedBy || manifest?.accepted_by || '',
      
      // Physical Verification Summary
      packagesExpected: manifest?.total_packages?.toString() || '',
      packagesReceived: acceptanceFormData?.packagesReceived || manifest?.total_packages?.toString() || '',
      weightExpected: manifest?.total_weight_kg?.toString() || '',
      weightReceived: manifest?.total_weight_kg?.toString() || '',
      dimensionsVerified: false,
      packagingIntegrityOK: true,
      sealsVerified: false,
      sealNumbers: '',
      
      // Quality Inspection Checklist
      packagingIntact: false,
      correctLabeling: false,
      noVisibleDamage: false,
      quantityMatches: false,
      meetsQualityStandards: false,
      allDocumentsComplete: false,
      inspectorName: '',
      inspectionDate: new Date().toISOString().split('T')[0],
      defectDescription: '',
      
      // Discrepancies & Issues
      shortageDetected: false,
      shortageDescription: '',
      damageDetected: false,
      damageDescription: '',
      qualityIssue: false,
      qualityIssueDescription: '',
      actionRequired: '',
      
      // Additional Notes
      additionalNotes: acceptanceFormData?.notes || acceptanceFormData?.additionalNotes || ''
    };
  };

  // Initialize with safe defaults
  const getDefaultFormData = () => {
    const manifest = manifestFullData || manifestData;
    const actualArrivalDate = manifest?.actual_arrival_date || manifest?.accepted_date || manifest?.actualArrival;
    const deliveryDate = acceptanceFormData?.dateReceived || actualArrivalDate;
    
    return {
      grnNumber: generateGRNNumber(),
      grnDate: new Date().toISOString().split('T')[0], // Always use today's date
      selectedPO: manifest?.po_number || '',
      warehouseLocation: getDefaultGrnWarehouseLocation(grnLogisticsFlow, manifestFullData, manifestData),
      supplierName: manifest?.from_company_name || '',
      supplierGSTIN: manifest?.from_gstin || '',
      contactPerson: manifest?.from_contact_person || '',
      phone: manifest?.from_phone || '',
      address: manifest?.from_address_line1 || '',
      deliveryChallanNo: (() => {
        // Try to get delivery challan from manifest documents
        if (manifest?.documents && Array.isArray(manifest.documents)) {
          const challanDoc = manifest.documents.find(doc => 
            doc.document_type?.toLowerCase().includes('challan') ||
            doc.document_type?.toLowerCase().includes('delivery')
          );
          if (challanDoc && challanDoc.document_name) {
            // Try to extract challan number from document name
            const challanMatch = challanDoc.document_name.match(/(?:DC|CHALLAN|DC-)([A-Z0-9-]+)/i);
            if (challanMatch) return challanMatch[0];
            return challanDoc.document_name;
          }
        }
        // Generate a default delivery challan number based on manifest number if available
        if (manifest?.manifest_number) {
          const year = new Date().getFullYear();
          return `DC-${year}-${manifest.manifest_number.split('-').pop() || '001'}`;
        }
        return '';
      })(),
      invoiceNumber: manifest?.invoice_bill_number || '',
      manifestNumber: manifest?.manifest_number || '',
      deliveryDate: deliveryDate ? new Date(deliveryDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      vehicleNumber: manifest?.vehicle_number || '',
      driverName: manifest?.driver_name || '',
      driverContact: manifest?.driver_contact_number || '',
      lrNumber: manifest?.lr_number || '',
      transportCompany: '',
      receivedBy: acceptanceFormData?.receivedBy || manifest?.accepted_by || '',
      packagesExpected: manifest?.total_packages?.toString() || '',
      packagesReceived: acceptanceFormData?.packagesReceived || manifest?.total_packages?.toString() || '',
      weightExpected: manifest?.total_weight_kg?.toString() || '',
      weightReceived: manifest?.total_weight_kg?.toString() || '',
      dimensionsVerified: false,
      packagingIntegrityOK: true,
      sealsVerified: false,
      sealNumbers: '',
      packagingIntact: false,
      correctLabeling: false,
      noVisibleDamage: false,
      quantityMatches: false,
      meetsQualityStandards: false,
      allDocumentsComplete: false,
      inspectorName: '',
      inspectionDate: new Date().toISOString().split('T')[0],
      defectDescription: '',
      shortageDetected: false,
      shortageDescription: '',
      damageDetected: false,
      damageDescription: '',
      qualityIssue: false,
      qualityIssueDescription: '',
      actionRequired: '',
      additionalNotes: acceptanceFormData?.notes || acceptanceFormData?.additionalNotes || ''
    };
  };

  const [formData, setFormData] = useState(getDefaultFormData());
  
  // Update form data when data is fetched
  useEffect(() => {
    if (!loading) {
      try {
        const newFormData = getInitialFormData();
        setFormData(newFormData);
      } catch (error) {
        console.error('Error initializing form data:', error);
        // Fallback to default form data
        setFormData(getDefaultFormData());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, grnData, manifestFullData, poData, editMode]);

  // Initialize items from GRN, manifest, or PO data
  const getInitialItems = () => {
    // If editing and GRN has items, use GRN items
    if (editMode && grnData && grnData.items && grnData.items.length > 0) {
      return grnData.items.map((item, index) => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const batchNumber = String(index + 1).padStart(3, '0');
        return {
          id: item.id || index + 1,
          sku: item.sku_item_code || '',
          description: item.description || '',
          hsn: item.hsn || '',
          uom: item.uom || '',
          qtyOrdered: item.qty_ordered?.toString() || '',
          qtyReceived: item.qty_received?.toString() || '',
          qtyAccepted: item.qty_accepted?.toString() || '',
          qtyRejected: item.qty_rejected?.toString() || '',
          batchLot: item.batch_lot || `BATCH-${year}-${month}-${batchNumber}`,
          mfgDate: item.mfg_date ? new Date(item.mfg_date).toISOString().split('T')[0] : '',
          expDate: item.exp_date ? new Date(item.exp_date).toISOString().split('T')[0] : '',
          condition: item.condition || 'Good',
          remarks: item.remarks || ''
        };
      });
    }
    
    // If creating new GRN, use manifest items
    const manifest = manifestFullData || manifestData;
    if (manifest?.items && manifest.items.length > 0) {
      return manifest.items.map((item, index) => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const batchNumber = String(index + 1).padStart(3, '0');
        const qtyOrdered = item.qty_total || item.qty_shipped || item.shipping_qty || '';
        const qtyReceived = item.qty_shipped || item.shipping_qty || qtyOrdered || '';
        return {
          id: index + 1,
          sku: item.hsn_product_code || '',
          description: item.product_description || '',
          hsn: item.hsn_product_code || '',
          uom: item.uom || '',
          qtyOrdered: qtyOrdered.toString(),
          qtyReceived: qtyReceived.toString(),
          qtyAccepted: qtyReceived.toString(),
          qtyRejected: '',
          batchLot: `BATCH-${year}-${month}-${batchNumber}`,
          mfgDate: '',
          expDate: '',
          condition: 'Good',
          remarks: ''
        };
      });
    }
    
    // If PO data is available, use PO line items
    if (poData && poData.line_items && poData.line_items.length > 0) {
      return poData.line_items.map((item, index) => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const batchNumber = String(index + 1).padStart(3, '0');
        return {
          id: index + 1,
          sku: item.product_code || '',
          description: item.product_name || item.description || '',
          hsn: item.hsn_code || '',
          uom: item.unit_of_measure || '',
          qtyOrdered: item.quantity?.toString() || '',
          qtyReceived: item.quantity?.toString() || '',
          qtyAccepted: item.quantity?.toString() || '',
          qtyRejected: '',
          batchLot: `BATCH-${year}-${month}-${batchNumber}`,
          mfgDate: '',
          expDate: '',
          condition: 'Good',
          remarks: ''
        };
      });
    }
    
    // Return empty array if no data
    return [];
  };

  const [items, setItems] = useState([]);
  
  // Update items when data is fetched
  useEffect(() => {
    if (!loading) {
      try {
        const initialItems = getInitialItems();
        setItems(initialItems);
      } catch (error) {
        console.error('Error initializing items:', error);
        setItems([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, grnData, manifestFullData, poData, editMode]);

  const [documents, setDocuments] = useState({
    proofOfDelivery: null,
    deliveryChallan: null,
    packingList: null,
    damagePhotos: null,
    inspectionReport: null,
    otherDocuments: null
  });
  
  // Load existing documents if editing
  useEffect(() => {
    if (editMode && grnData && grnData.documents && grnData.documents.length > 0) {
      const docsMap = {};
      grnData.documents.forEach(doc => {
        const docType = doc.document_type?.toLowerCase().replace(/\s+/g, '');
        if (docType === 'proofofdelivery' || docType === 'pod') {
          docsMap.proofOfDelivery = { name: doc.document_name, path: doc.document_path };
        } else if (docType === 'deliverychallan' || docType === 'challan') {
          docsMap.deliveryChallan = { name: doc.document_name, path: doc.document_path };
        } else if (docType === 'packinglist') {
          docsMap.packingList = { name: doc.document_name, path: doc.document_path };
        } else if (docType === 'damagephotos' || docType === 'damage') {
          docsMap.damagePhotos = { name: doc.document_name, path: doc.document_path };
        } else if (docType === 'inspectionreport' || docType === 'inspection') {
          docsMap.inspectionReport = { name: doc.document_name, path: doc.document_path };
        } else {
          docsMap.otherDocuments = { name: doc.document_name, path: doc.document_path };
        }
      });
      // Note: We can't set File objects from paths, but we can display the file names
      // For actual file handling, you'd need to download them
    }
  }, [editMode, grnData]);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Master Data Hooks
  const { attributeUnitOptions, loading: uomLoading } = useProductDetailsMaster();

  // Upload a single file
  const uploadFile = async (file, docType) => {
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      // Get company name from form data or use default
      const companyName = formData.supplierName?.trim() || 'GRN_Company';
      if (!companyName || companyName === '') {
        throw new Error('Company name is required for file upload');
      }
      
      // Use 'company' category (valid category) for GRN documents
      const category = 'company';
      
      // Map document types to file types
      const fileTypeMap = {
        proofOfDelivery: 'pod',
        deliveryChallan: 'delivery_challan',
        packingList: 'packing_list',
        damagePhotos: 'damage_photos',
        inspectionReport: 'inspection_report',
        otherDocuments: 'other_document'
      };
      
      const fileType = fileTypeMap[docType] || docType;
      
      uploadFormData.append('category', category);
      uploadFormData.append('companyName', companyName);
      uploadFormData.append('fileType', fileType);
      
      const response = await CommonService.post('/upload/single', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response && response.success && response.data && response.data.filePath) {
        return response.data.filePath;
      } else {
        throw new Error(response?.message || 'File upload failed');
      }
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error);
      throw error;
    }
  };

  // Upload all documents
  const uploadAllDocuments = async () => {
    const uploadedDocuments = [];
    
    const documentTypes = [
      { key: 'proofOfDelivery', type: 'proof_of_delivery', name: 'Proof of Delivery' },
      { key: 'deliveryChallan', type: 'delivery_challan', name: 'Delivery Challan' },
      { key: 'packingList', type: 'packing_list', name: 'Packing List' },
      { key: 'damagePhotos', type: 'damage_photos', name: 'Damage Photos' },
      { key: 'inspectionReport', type: 'inspection_report', name: 'Inspection Report' },
      { key: 'otherDocuments', type: 'other_documents', name: 'Other Documents' }
    ];
    
    for (const docType of documentTypes) {
      if (documents[docType.key]) {
        try {
          const filePath = await uploadFile(documents[docType.key], docType.key);
          uploadedDocuments.push({
            document_type: docType.type,
            document_name: docType.name,
            document_path: filePath
          });
        } catch (error) {
          console.error(`Failed to upload ${docType.name}:`, error);
          throw new Error(`Failed to upload ${docType.name}`);
        }
      }
    }
    
    return uploadedDocuments;
  };

  // Prepare GRN data for API
  const prepareGRNData = (status = 'Draft') => {
    const manifestIdForGrn = resolveManifestRecordId(manifestFullData, manifestData);
    const forceNullPurchaseOrderForManifestGrn =
      Boolean(manifestIdForGrn) && (allowGrnWithoutPurchaseOrder || manifestOnlyGrn);

    // Do not attach a PO id for logistics / manifest-only GRNs (avoids PO branch on API when manifest is the source of truth)
    let purchaseOrderId = null;
    if (!forceNullPurchaseOrderForManifestGrn) {
      if (poData && poData.id) {
        purchaseOrderId = poData.id;
      } else if (formData.selectedPO) {
        const poIdMatch = formData.selectedPO.match(/PO-(\d+)/);
        if (poIdMatch) {
          purchaseOrderId = parseInt(poIdMatch[1], 10);
        }
      }
    }

    const manifestSrc = manifestFullData || manifestData || {};
    const logisticsIdOrNull = (v) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    };
    if (!purchaseOrderId && !allowGrnWithoutPurchaseOrder) {
      throw new Error('Purchase Order ID is required. Please ensure a valid PO is selected.');
    }
    
    // Prepare quality inspection checklist
    const qualityChecklist = {
      packagingIntact: formData.packagingIntact || false,
      correctLabeling: formData.correctLabeling || false,
      noVisibleDamage: formData.noVisibleDamage || false,
      quantityMatches: formData.quantityMatches || false,
      meetsQualityStandards: formData.meetsQualityStandards || false,
      allDocumentsComplete: formData.allDocumentsComplete || false
    };

    // Prepare items
    const grnItems = items.map((item, index) => ({
      item_sequence: index + 1,
      sku_item_code: item.sku || null,
      description: item.description || '',
      hsn: item.hsn || null,
      uom: item.uom || '',
      qty_ordered: parseFloat(item.qtyOrdered) || 0,
      qty_received: parseFloat(item.qtyReceived) || 0,
      qty_accepted: parseFloat(item.qtyAccepted) || 0,
      qty_rejected: parseFloat(item.qtyRejected) || 0,
      batch_lot: item.batchLot || null,
      mfg_date: item.mfgDate || null,
      exp_date: item.expDate || null,
      condition: item.condition || null,
      remarks: item.remarks || null
    }));

    const grnData = {
      purchase_order_id: forceNullPurchaseOrderForManifestGrn ? null : purchaseOrderId ?? null,
      invoice_id: null,
      manifest_id: manifestIdForGrn,
      logistics_generic_rfq_id: logisticsIdOrNull(
        manifestSrc.logistics_generic_rfq_id ?? manifestSrc.logisticsGenericRfqId
      ),
      logistics_quote_id: logisticsIdOrNull(manifestSrc.logistics_quote_id ?? manifestSrc.logisticsQuoteId),
      grn_number: formData.grnNumber,
      grn_date: formData.grnDate,
      status: status,
      warehouse_location: formData.warehouseLocation || null,
      supplier_name: formData.supplierName || '',
      supplier_gstin: formData.supplierGSTIN || null,
      contact_person: formData.contactPerson || null,
      phone: formData.phone || null,
      address: formData.address || null,
      delivery_challan_no: formData.deliveryChallanNo || null,
      invoice_number:
        grnLogisticsFlow || isLogisticsFlowManifest(manifestSrc) ? null : formData.invoiceNumber || null,
      manifest_number: formData.manifestNumber || null,
      delivery_date: formData.deliveryDate || null,
      vehicle_number: formData.vehicleNumber || null,
      driver_name: formData.driverName || null,
      driver_contact: formData.driverContact || null,
      lr_number: formData.lrNumber || null,
      transport_company: formData.transportCompany || null,
      received_by_name: formData.receivedBy || null,
      packages_expected: formData.packagesExpected ? parseInt(formData.packagesExpected) : null,
      packages_received: formData.packagesReceived ? parseInt(formData.packagesReceived) : null,
      weight_expected_kg: formData.weightExpected ? parseFloat(formData.weightExpected) : null,
      weight_received_kg: formData.weightReceived ? parseFloat(formData.weightReceived) : null,
      dimensions_verified: formData.dimensionsVerified ? 1 : 0,
      packaging_integrity_ok: formData.packagingIntegrityOK ? 1 : 0,
      seals_verified: formData.sealsVerified ? 1 : 0,
      seal_numbers: formData.sealNumbers || null,
      quality_inspection_checklist: qualityChecklist,
      inspector_name: formData.inspectorName || null,
      inspection_date: formData.inspectionDate || null,
      defect_issue_description: formData.defectDescription || null,
      shortage_detected: formData.shortageDetected ? 1 : 0,
      damage_detected: formData.damageDetected ? 1 : 0,
      quality_issue: formData.qualityIssue ? 1 : 0,
      total_items: totalItems,
      total_qty_accepted: totalQtyAccepted,
      total_qty_rejected: totalQtyRejected,
      acceptance_rate: parseFloat(acceptanceRate),
      additional_notes: formData.additionalNotes || null,
      // Company IDs - get from PO or manifest
      buyer_id: poData?.created_user_id || null,
      buyer_company_id: poData?.buyer_company_id || manifestFullData?.buyer_company_id || manifestData?.buyer_company_id || null,
      seller_company_id: poData?.seller_company_id || manifestFullData?.seller_company_id || manifestData?.seller_company_id || null,
      items: grnItems
    };

    return grnData;
  };

  // Save as Draft
  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      
      // Validate required fields
      if (!allowGrnWithoutPurchaseOrder && !formData.selectedPO) {
        toast.error('Please select a Purchase Order');
        setSaving(false);
        return;
      }

      if (!formData.warehouseLocation) {
        toast.error('Please select a Warehouse Location');
        setSaving(false);
        return;
      }

      if (!formData.deliveryChallanNo) {
        toast.error('Please enter Delivery Challan No.');
        setSaving(false);
        return;
      }

      // Upload documents first
      let uploadedDocuments = [];
      try {
        uploadedDocuments = await uploadAllDocuments();
      } catch (uploadError) {
        console.warn('Some documents failed to upload, continuing with draft save...', uploadError);
      }

      // Prepare GRN data
      const grnData = prepareGRNData('Draft');
      grnData.documents = uploadedDocuments;

      let response;
      const draftIsUpdate = Boolean(editMode && grnId);
      console.log('Save Draft:', {
        path: draftIsUpdate ? 'update_existing' : 'create_new',
        grnId: grnId ?? null,
        editMode,
        grnNumber: grnData.grn_number,
      });

      if (draftIsUpdate) {
        // Update existing GRN - don't send grn_number as it's immutable
        const updateData = { ...grnData };
        // Remove grn_number from update data as it should not be changed
        delete updateData.grn_number;
        response = await CommonService.put(`/grn/${grnId}`, updateData);
      } else {
        response = await CommonService.post('/grn', grnData);
      }

      if (response && response.success) {
        toast.success('GRN saved as draft successfully');
        setTimeout(() => {
          navigate(returnToLogisticsSent ? '/logistics-rfqs-sent' : '/grn-manifest-list');
        }, 1000);
      } else {
        throw new Error(response?.error || 'Failed to save GRN');
      }
    } catch (error) {
      console.error('Error saving GRN as draft:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to save GRN as draft');
    } finally {
      setSaving(false);
    }
  };

  // Submit GRN
  const handleSubmitGRN = async () => {
    try {
      setSubmitting(true);
      
      // Validate required fields
      if (!allowGrnWithoutPurchaseOrder && !formData.selectedPO) {
        toast.error('Please select a Purchase Order');
        setSubmitting(false);
        return;
      }

      if (!formData.warehouseLocation) {
        toast.error('Please select a Warehouse Location');
        setSubmitting(false);
        return;
      }

      if (!formData.deliveryChallanNo) {
        toast.error('Please enter Delivery Challan No.');
        setSubmitting(false);
        return;
      }

      if (!formData.deliveryDate) {
        toast.error('Please enter Delivery Date');
        setSubmitting(false);
        return;
      }

      if (!formData.packagesReceived) {
        toast.error('Please enter Packages Received');
        setSubmitting(false);
        return;
      }

      if (!formData.weightReceived) {
        toast.error('Please enter Weight Received');
        setSubmitting(false);
        return;
      }

      if (!formData.inspectorName) {
        toast.error('Please enter Inspector Name');
        setSubmitting(false);
        return;
      }

      if (!formData.inspectionDate) {
        toast.error('Please enter Inspection Date');
        setSubmitting(false);
        return;
      }

      if (items.length === 0) {
        toast.error('Please add at least one item');
        setSubmitting(false);
        return;
      }

      // Validate required file uploads
      if (!documents.proofOfDelivery) {
        toast.error('Please upload Proof of Delivery (POD)');
        setSubmitting(false);
        return;
      }

      if (!documents.deliveryChallan) {
        toast.error('Please upload Delivery Challan');
        setSubmitting(false);
        return;
      }

      // Upload documents
      let uploadedDocuments = [];
      try {
        uploadedDocuments = await uploadAllDocuments();
      } catch (uploadError) {
        console.warn('Some documents failed to upload:', uploadError);
        // Continue with submission even if some documents fail
      }

      // Prepare GRN data
      const grnData = prepareGRNData('Pending Approval');
      grnData.documents = uploadedDocuments;

      let response;
      const submitIsUpdate = Boolean(editMode && grnId);
      console.log('Submit GRN:', {
        path: submitIsUpdate ? 'update_existing' : 'create_new',
        grnId: grnId ?? null,
        editMode,
        grnNumber: grnData.grn_number,
      });

      if (submitIsUpdate) {
        // Update existing GRN - don't send grn_number as it's immutable
        const updateData = { ...grnData };
        // Remove grn_number from update data as it should not be changed
        delete updateData.grn_number;
        response = await CommonService.put(`/grn/${grnId}`, updateData);
      } else {
        response = await CommonService.post('/grn', grnData);
      }

      if (response && response.success) {
        toast.success('GRN submitted successfully');
        setIsPreviewModalOpen(false);
        setTimeout(() => {
          navigate(returnToLogisticsSent ? '/logistics-rfqs-sent' : '/grn-manifest-list');
        }, 1000);
      } else {
        throw new Error(response?.error || 'Failed to submit GRN');
      }
    } catch (error) {
      console.error('Error submitting GRN:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to submit GRN');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="grn-generate">
        <div className="grn-main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  const handleInputChange = async (e) => {
    const { name, value, type, checked } = e.target;
    
    // If PO is selected, fetch PO details and populate supplier info and items
    if (name === 'selectedPO' && value) {
      try {
        // Extract PO ID from value (format: PO-{id})
        const poIdMatch = value.match(/PO-(\d+)/);
        if (poIdMatch) {
          const poId = parseInt(poIdMatch[1]);
          const poResponse = await CommonService.get(`/purchase-order/${poId}`);
          if (poResponse && poResponse.success && poResponse.data) {
            const selectedPOData = poResponse.data;
            setPoData(selectedPOData);
            
            // Update supplier details from PO
            setFormData(prev => ({
              ...prev,
              [name]: value,
              supplierName: selectedPOData.seller_company_name || prev.supplierName,
              supplierGSTIN: selectedPOData.seller_company_gst_number || prev.supplierGSTIN,
              contactPerson: selectedPOData.seller_contact_person || prev.contactPerson,
              phone: selectedPOData.seller_phone || prev.phone,
              address: selectedPOData.seller_company_address || prev.address
            }));
            
            // Update items from PO line items if available
            if (selectedPOData.line_items && selectedPOData.line_items.length > 0) {
              const date = new Date();
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              
              const poItems = selectedPOData.line_items.map((item, index) => {
                const batchNumber = String(index + 1).padStart(3, '0');
                return {
                  id: index + 1,
                  sku: item.product_code || '',
                  description: item.product_name || item.description || '',
                  hsn: item.hsn_code || '',
                  uom: item.unit_of_measure || '',
                  qtyOrdered: item.quantity?.toString() || '',
                  qtyReceived: item.quantity?.toString() || '',
                  qtyAccepted: item.quantity?.toString() || '',
                  qtyRejected: '',
                  batchLot: `BATCH-${year}-${month}-${batchNumber}`,
                  mfgDate: '',
                  expDate: '',
                  condition: 'Good',
                  remarks: ''
                };
              });
              setItems(poItems);
            }
          } else {
            toast.error('Failed to fetch PO details');
          }
        } else {
          // If value doesn't match PO-{id} format, just update the field
          setFormData(prev => ({
            ...prev,
            [name]: value
          }));
        }
      } catch (error) {
        console.error('Error fetching PO details:', error);
        toast.error('Failed to fetch PO details');
        // Still update the field value
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      // For other fields, just update normally
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleItemChange = (id, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        
        // Auto-populate Qty Received from Qty Ordered when Qty Ordered is set
        if (field === 'qtyOrdered' && value) {
          updated.qtyReceived = value;
        }
        
        // Auto-calculate the remaining field based on Qty Received
        if (field === 'qtyAccepted') {
          const received = parseFloat(item.qtyReceived) || 0;
          const accepted = parseFloat(value) || 0;
          const rejected = Math.max(0, received - accepted);
          updated.qtyRejected = rejected > 0 ? rejected.toString() : '';
        } else if (field === 'qtyRejected') {
          const received = parseFloat(item.qtyReceived) || 0;
          const rejected = parseFloat(value) || 0;
          const accepted = Math.max(0, received - rejected);
          updated.qtyAccepted = accepted > 0 ? accepted.toString() : '';
        }
        
        return updated;
      }
      return item;
    }));
  };

  const handleAddItem = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const batchNumber = String(items.length + 1).padStart(3, '0');
    const newItem = {
      id: items.length + 1,
      sku: '',
      description: '',
      hsn: '',
      uom: '',
      qtyOrdered: '',
      qtyReceived: '',
      qtyAccepted: '',
      qtyRejected: '',
      batchLot: `BATCH-${year}-${month}-${batchNumber}`,
      mfgDate: '',
      expDate: '',
      condition: 'Good',
      remarks: ''
    };
    setItems([...items, newItem]);
  };

  const handleDeleteItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleFileUpload = (docType, file) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: file
    }));
  };

  const calculateTotals = () => {
    const totalItems = items.length;
    const totalQtyOrdered = items.reduce((sum, item) => sum + (parseFloat(item.qtyOrdered) || 0), 0);
    const totalQtyReceived = items.reduce((sum, item) => sum + (parseFloat(item.qtyReceived) || 0), 0);
    const totalQtyAccepted = items.reduce((sum, item) => sum + (parseFloat(item.qtyAccepted) || 0), 0);
    const totalQtyRejected = items.reduce((sum, item) => sum + (parseFloat(item.qtyRejected) || 0), 0);
    const acceptanceRate = totalQtyReceived > 0 ? ((totalQtyAccepted / totalQtyReceived) * 100).toFixed(1) : '0.0';
    return { totalItems, totalQtyOrdered, totalQtyReceived, totalQtyAccepted, totalQtyRejected, acceptanceRate };
  };

  const { totalItems, totalQtyOrdered, totalQtyReceived, totalQtyAccepted, totalQtyRejected, acceptanceRate } = calculateTotals();

  // Use master data for UOM options
  const uomOptions = attributeUnitOptions || [];

  // Helper function to get UOM label from value (ID or label)
  const getUomLabel = (unitValue) => {
    if (!unitValue) return null;
    
    // First try to find by value (ID)
    const foundByValue = attributeUnitOptions?.find(opt => 
      String(opt.value) === String(unitValue) || 
      Number(opt.value) === Number(unitValue)
    );
    if (foundByValue) {
      return foundByValue.label || foundByValue.value || unitValue;
    }
    
    // If not found, it might already be a label
    const foundByLabel = attributeUnitOptions?.find(opt => 
      opt.label === String(unitValue)
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    // If still not found, return the value as is
    return unitValue;
  };

  const conditionOptions = [
    { value: 'Good', label: 'Good' },
    { value: 'Damaged', label: 'Damaged' },
    { value: 'Expired', label: 'Expired' },
    { value: 'Defective', label: 'Defective' },
    { value: 'Partial', label: 'Partial' }
  ];

  const grnRecordId =
    grnId != null && String(grnId).trim() !== ''
      ? String(grnId)
      : grnData?.id != null
        ? String(grnData.id)
        : null;

  return (
    <div className="grn-generate">
      <div className="grn-main-content">
        {grnRecordId && (
          <LinkedLogisticsPanel
            sourceModule="buyer_seller"
            sourceRecordType="grn_header"
            sourceRecordId={grnRecordId}
            linkPurpose="fulfillment"
          />
        )}
        {/* Header */}
        <div className="grn-header">
          <div className="grn-header-left">
            <nav aria-label="breadcrumb" className="grn-breadcrumb">
              <ol className="grn-breadcrumb-list">
                <li className="grn-breadcrumb-item">
                  <a href="/" className="grn-breadcrumb-link">Home</a>
                </li>
                <li className="grn-breadcrumb-item grn-breadcrumb-active" aria-current="page">
                  Goods Received Note
                </li>
              </ol>
            </nav>
            <h1 className="grn-main-title">Generate Goods Received Note (GRN)</h1>
            <p className="grn-subtitle">Create and manage goods receipt documentation</p>
          </div>
          <div className="grn-header-right">
            <div className="grn-header-actions">
              <button className="grn-back-btn" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
                Back
              </button>
              <button 
                className="grn-btn grn-btn-draft" 
                onClick={handleSaveDraft}
                disabled={saving || submitting}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                {saving ? 'Saving...' : 'Save as Draft'}
              </button>
              <button className="grn-btn grn-btn-primary grn-btn-submit" onClick={() => setIsPreviewModalOpen(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                Preview & Submit
              </button>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="grn-main-card">
          {/* GRN Header Information */}
          <div className="grn-section-wrapper">
            <div className="grn-section-header-with-bar">
              <h3 className="grn-section-title grn-section-title-blue">GRN Header Information</h3>
            </div>
            <div className="grn-section grn-section-blue">
              <div className="grn-section-content">
                <div className="grn-form-grid grn-form-grid-4col">
                  <div className="grn-form-field">
                    <label>GRN Number (Auto)</label>
                    <input
                      type="text"
                      className="grn-input grn-input-readonly"
                      name="grnNumber"
                      value={formData.grnNumber}
                      readOnly
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>GRN Date</label>
                    <input
                      type="date"
                      className="grn-input grn-input-date grn-input-readonly"
                      name="grnDate"
                      value={formData.grnDate}
                      readOnly
                    />
                  </div>
                  {!hideGrnPoInvoiceFields ? (
                    <div className="grn-form-field">
                      <label>
                        PO Number <span className="grn-required">*</span>
                      </label>
                      {purchaseOrders.length > 0 ? (
                        <input
                          type="text"
                          className="grn-input grn-input-readonly"
                          name="selectedPO"
                          value={
                            formData.selectedPO ||
                            (purchaseOrders[0] ? purchaseOrders[0].po_number || `PO-${purchaseOrders[0].id}` : '')
                          }
                          readOnly
                          placeholder={loadingPOs ? 'Loading...' : ''}
                        />
                      ) : (
                        <input
                          type="text"
                          className="grn-input grn-input-readonly"
                          name="selectedPO"
                          value={formData.selectedPO || ''}
                          readOnly
                          placeholder={loadingPOs ? 'Loading...' : 'No PO available'}
                        />
                      )}
                      {loadingPOs && (
                        <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                          Loading purchase order...
                        </small>
                      )}
                    </div>
                  ) : null}
                  <div className="grn-form-field">
                    <label>Warehouse Location <span className="grn-required">*</span></label>
                    <select
                      className="grn-input grn-input-select"
                      name="warehouseLocation"
                      value={formData.warehouseLocation}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Warehouse Location</option>
                      <option value="Main Warehouse - Bangalore">Main Warehouse - Bangalore</option>
                      <option value="Secondary Warehouse - Mumbai">Secondary Warehouse - Mumbai</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Supplier / Vendor Details */}
          <div className="grn-section-wrapper">
            <div className="grn-section-header-with-bar">
              <h3 className="grn-section-title grn-section-title-orange">Supplier / Vendor Details (From PO)</h3>
            </div>
            <div className="grn-section grn-section-orange">
              <div className="grn-section-content">
                <div className="grn-form-grid grn-form-grid-4col">
                  <div className="grn-form-field">
                    <label>Supplier Name</label>
                    <input
                      type="text"
                      className="grn-input grn-input-readonly"
                      name="supplierName"
                      value={formData.supplierName}
                      readOnly
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>GSTIN</label>
                    <input
                      type="text"
                      className="grn-input grn-input-readonly"
                      name="supplierGSTIN"
                      value={formData.supplierGSTIN}
                      readOnly
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Contact Person</label>
                    <input
                      type="text"
                      className="grn-input grn-input-readonly"
                      name="contactPerson"
                      value={formData.contactPerson}
                      readOnly
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Phone</label>
                    <input
                      type="text"
                      className="grn-input grn-input-readonly"
                      name="phone"
                      value={formData.phone}
                      readOnly
                    />
                  </div>
                </div>
                <div className="grn-form-grid grn-form-grid-1col">
                  <div className="grn-form-field">
                    <label>Address</label>
                    <input
                      type="text"
                      className="grn-input grn-input-readonly"
                      name="address"
                      value={formData.address}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery & Transport Information */}
          <div className="grn-section-wrapper">
            <div className="grn-section-header-with-bar">
              <h3 className="grn-section-title grn-section-title-purple">Delivery & Transport Information</h3>
            </div>
            <div className="grn-section grn-section-purple">
              <div className="grn-section-content">
                <div className="grn-form-grid grn-form-grid-4col">
                  <div className="grn-form-field">
                    <label>Delivery Challan No. <span className="grn-required">*</span></label>
                    <input
                      type="text"
                      className="grn-input"
                      name="deliveryChallanNo"
                      value={formData.deliveryChallanNo}
                      onChange={handleInputChange}
                    />
                  </div>
                  {!hideGrnPoInvoiceFields ? (
                    <div className="grn-form-field">
                      <label>Invoice Number</label>
                      <input
                        type="text"
                        className="grn-input grn-input-readonly"
                        name="invoiceNumber"
                        value={formData.invoiceNumber}
                        readOnly
                      />
                    </div>
                  ) : null}
                  <div className="grn-form-field">
                    <label>Manifest Number</label>
                    <input
                      type="text"
                      className="grn-input grn-input-readonly"
                      name="manifestNumber"
                      value={formData.manifestNumber}
                      readOnly
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Delivery Date <span className="grn-required">*</span></label>
                    <input
                      type="date"
                      className="grn-input grn-input-date"
                      name="deliveryDate"
                      value={formData.deliveryDate}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Vehicle Number</label>
                    <input
                      type="text"
                      className="grn-input"
                      name="vehicleNumber"
                      value={formData.vehicleNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Driver Name</label>
                    <input
                      type="text"
                      className="grn-input"
                      name="driverName"
                      value={formData.driverName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Driver Contact</label>
                    <input
                      type="text"
                      className="grn-input"
                      name="driverContact"
                      value={formData.driverContact}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>LR Number</label>
                    <input
                      type="text"
                      className="grn-input grn-input-readonly"
                      name="lrNumber"
                      value={formData.lrNumber}
                      readOnly
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Transport Company</label>
                    <input
                      type="text"
                      className="grn-input"
                      name="transportCompany"
                      value={formData.transportCompany}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Received Items Details */}
          <div className="grn-section-wrapper">
            <div className="grn-section-header-with-bar">
              <h3 className="grn-section-title grn-section-title-orange">Received Items Details</h3>
              <button className="grn-btn grn-btn-add-item" onClick={handleAddItem}>+ Add Item</button>
            </div>
            <div className="grn-section grn-section-items">
              <div className="grn-table-wrapper">
                <table className="grn-items-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>HSN</th>
                      <th>UOM</th>
                      <th>Qty Ordered</th>
                      <th>Qty Received</th>
                      <th>Qty Accepted</th>
                      <th>Qty Rejected</th>
                      <th>Batch/Lot</th>
                      <th>Mfg Date</th>
                      <th>Exp Date</th>
                      <th>Condition</th>
                      <th>Remarks</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.id}>
                        <td>
                          <input
                            type="text"
                            className="grn-table-input"
                            value={item.description}
                            onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="grn-table-input"
                            value={item.hsn}
                            onChange={(e) => handleItemChange(item.id, 'hsn', e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className="grn-table-input grn-table-select"
                            value={item.uom}
                            onChange={(e) => handleItemChange(item.id, 'uom', e.target.value)}
                          >
                            <option value="">Select UOM</option>
                            {uomOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="grn-table-input grn-table-input-readonly"
                            value={item.qtyOrdered}
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="grn-table-input"
                            value={item.qtyReceived}
                            onChange={(e) => handleItemChange(item.id, 'qtyReceived', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="grn-table-input"
                            value={item.qtyAccepted}
                            onChange={(e) => handleItemChange(item.id, 'qtyAccepted', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="grn-table-input"
                            value={item.qtyRejected}
                            onChange={(e) => handleItemChange(item.id, 'qtyRejected', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="grn-table-input"
                            value={item.batchLot}
                            onChange={(e) => handleItemChange(item.id, 'batchLot', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className="grn-table-input grn-table-input-date"
                            value={item.mfgDate}
                            onChange={(e) => handleItemChange(item.id, 'mfgDate', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className="grn-table-input grn-table-input-date"
                            value={item.expDate}
                            onChange={(e) => handleItemChange(item.id, 'expDate', e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className="grn-table-input grn-table-select"
                            value={item.condition}
                            onChange={(e) => handleItemChange(item.id, 'condition', e.target.value)}
                          >
                            {conditionOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="grn-table-input"
                            value={item.remarks}
                            onChange={(e) => handleItemChange(item.id, 'remarks', e.target.value)}
                            placeholder="Remarks"
                          />
                        </td>
                        <td>
                          <button
                            className="grn-btn-delete"
                            onClick={() => handleDeleteItem(item.id)}
                            type="button"
                            title="Remove item"
                            aria-label="Delete item"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-delete-icon">
                              <path d="M10 11v6"></path>
                              <path d="M14 11v6"></path>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                              <path d="M3 6h18"></path>
                              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="grn-table-totals-row">
                      <td colSpan="3" className="grn-totals-label">Totals:</td>
                      <td className="grn-total-value">{totalQtyOrdered}</td>
                      <td className="grn-total-value">{totalQtyReceived}</td>
                      <td className="grn-total-value grn-total-accepted">{totalQtyAccepted}</td>
                      <td className="grn-total-value grn-total-rejected">{totalQtyRejected}</td>
                      <td colSpan="6"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="grn-table-note">All fields are editable. Update quantities received, accepted, and rejected as per actual inspection.</p>
            </div>
          </div>

          {/* Physical Verification Summary */}
          <div className="grn-section-wrapper">
            <div className="grn-section-header-with-bar">
              <h3 className="grn-section-title grn-section-title-indigo">Physical Verification Summary</h3>
            </div>
            <div className="grn-section grn-section-verification">
              <div className="grn-section-content">
                <div className="grn-form-grid grn-form-grid-4col">
                  <div className="grn-form-field">
                    <label>Packages Expected</label>
                    <input
                      type="number"
                      className="grn-input"
                      name="packagesExpected"
                      value={formData.packagesExpected}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Packages Received <span className="grn-required">*</span></label>
                    <input
                      type="number"
                      className="grn-input"
                      name="packagesReceived"
                      value={formData.packagesReceived}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Weight Expected (kg)</label>
                    <input
                      type="text"
                      className="grn-input"
                      name="weightExpected"
                      value={formData.weightExpected}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Weight Received (kg) <span className="grn-required">*</span></label>
                    <input
                      type="text"
                      className="grn-input"
                      name="weightReceived"
                      value={formData.weightReceived}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="grn-checkbox-group">
                  <label className="grn-checkbox-label">
                    <input
                      type="checkbox"
                      name="dimensionsVerified"
                      checked={formData.dimensionsVerified}
                      onChange={handleInputChange}
                      className="grn-checkbox"
                    />
                    <span>Dimensions Verified</span>
                  </label>
                  <label className="grn-checkbox-label">
                    <input
                      type="checkbox"
                      name="packagingIntegrityOK"
                      checked={formData.packagingIntegrityOK}
                      onChange={handleInputChange}
                      className="grn-checkbox"
                    />
                    <span>Packaging Integrity OK</span>
                  </label>
                  <label className="grn-checkbox-label">
                    <input
                      type="checkbox"
                      name="sealsVerified"
                      checked={formData.sealsVerified}
                      onChange={handleInputChange}
                      className="grn-checkbox"
                    />
                    <span>Seals Verified</span>
                  </label>
                </div>
                {formData.sealsVerified && (
                  <div className="grn-form-field">
                    <label>Seal Numbers</label>
                    <input
                      type="text"
                      className="grn-input"
                      name="sealNumbers"
                      value={formData.sealNumbers}
                      onChange={handleInputChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quality Inspection Checklist */}
          <div className="grn-section-wrapper">
            <div className="grn-section-header-with-bar">
              <h3 className="grn-section-title grn-section-title-green">Quality Inspection Checklist</h3>
            </div>
            <div className="grn-section grn-section-inspection">
              <div className="grn-section-content">
                <div className="grn-inspection-checklist">
                  <label className="grn-checkbox-label">
                    <input
                      type="checkbox"
                      name="packagingIntact"
                      checked={formData.packagingIntact}
                      onChange={handleInputChange}
                      className="grn-checkbox"
                    />
                    <span>Packaging is intact and undamaged</span>
                  </label>
                  <label className="grn-checkbox-label">
                    <input
                      type="checkbox"
                      name="correctLabeling"
                      checked={formData.correctLabeling}
                      onChange={handleInputChange}
                      className="grn-checkbox"
                    />
                    <span>Correct labeling and marking</span>
                  </label>
                  <label className="grn-checkbox-label">
                    <input
                      type="checkbox"
                      name="noVisibleDamage"
                      checked={formData.noVisibleDamage}
                      onChange={handleInputChange}
                      className="grn-checkbox"
                    />
                    <span>No visible damage to goods</span>
                  </label>
                  <label className="grn-checkbox-label">
                    <input
                      type="checkbox"
                      name="quantityMatches"
                      checked={formData.quantityMatches}
                      onChange={handleInputChange}
                      className="grn-checkbox"
                    />
                    <span>Quantity matches PO and delivery challan</span>
                  </label>
                  <label className="grn-checkbox-label">
                    <input
                      type="checkbox"
                      name="meetsQualityStandards"
                      checked={formData.meetsQualityStandards}
                      onChange={handleInputChange}
                      className="grn-checkbox"
                    />
                    <span>Meets quality standards</span>
                  </label>
                  <label className="grn-checkbox-label">
                    <input
                      type="checkbox"
                      name="allDocumentsComplete"
                      checked={formData.allDocumentsComplete}
                      onChange={handleInputChange}
                      className="grn-checkbox"
                    />
                    <span>All documents are complete</span>
                  </label>
                </div>
                <div className="grn-form-grid grn-form-grid-3col">
                  <div className="grn-form-field">
                    <label>Inspector Name <span className="grn-required">*</span></label>
                    <input
                      type="text"
                      className="grn-input"
                      name="inspectorName"
                      value={formData.inspectorName}
                      onChange={handleInputChange}
                      placeholder="Name of QC inspector"
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Inspection Date <span className="grn-required">*</span></label>
                    <input
                      type="date"
                      className="grn-input grn-input-date"
                      name="inspectionDate"
                      value={formData.inspectionDate}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="grn-form-field">
                    <label>Defect / Issue Description</label>
                    <textarea
                      className="grn-textarea grn-textarea-small"
                      name="defectDescription"
                      value={formData.defectDescription}
                      onChange={handleInputChange}
                      placeholder="Describe any defects or quality issues found..."
                      rows="2"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Discrepancies & Issues */}
          <div className="grn-section-wrapper">
            <div className="grn-section-header-with-bar">
              <h3 className="grn-section-title grn-section-title-red">Discrepancies & Issues</h3>
            </div>
            <div className="grn-section grn-section-discrepancies">
              <div className="grn-section-content">
                <div className="grn-discrepancies-grid">
                  <div className="grn-discrepancy-card">
                    <div className="grn-discrepancy-header">
                      <label className="grn-checkbox-label">
                        <input
                          type="checkbox"
                          name="shortageDetected"
                          checked={formData.shortageDetected}
                          onChange={handleInputChange}
                          className="grn-checkbox"
                        />
                        <span>Shortage Detected</span>
                      </label>
                    </div>
                    {formData.shortageDetected && (
                      <textarea
                        className="grn-textarea grn-textarea-small"
                        name="shortageDescription"
                        value={formData.shortageDescription}
                        onChange={handleInputChange}
                        placeholder="Describe shortage details (items, quantities, etc.)..."
                        rows="2"
                      />
                    )}
                  </div>
                  <div className="grn-discrepancy-card">
                    <div className="grn-discrepancy-header">
                      <label className="grn-checkbox-label">
                        <input
                          type="checkbox"
                          name="damageDetected"
                          checked={formData.damageDetected}
                          onChange={handleInputChange}
                          className="grn-checkbox"
                        />
                        <span>Damage Detected</span>
                      </label>
                    </div>
                    {formData.damageDetected && (
                      <textarea
                        className="grn-textarea grn-textarea-small"
                        name="damageDescription"
                        value={formData.damageDescription}
                        onChange={handleInputChange}
                        placeholder="Describe damage details (type, extent, affected items, etc.)..."
                        rows="2"
                      />
                    )}
                  </div>
                  <div className="grn-discrepancy-card">
                    <div className="grn-discrepancy-header">
                      <label className="grn-checkbox-label">
                        <input
                          type="checkbox"
                          name="qualityIssue"
                          checked={formData.qualityIssue}
                          onChange={handleInputChange}
                          className="grn-checkbox"
                        />
                        <span>Quality Issue</span>
                      </label>
                    </div>
                    {formData.qualityIssue && (
                      <textarea
                        className="grn-textarea grn-textarea-small"
                        name="qualityIssueDescription"
                        value={formData.qualityIssueDescription}
                        onChange={handleInputChange}
                        placeholder="Describe quality issues (specs not met, defects, etc.)..."
                        rows="2"
                      />
                    )}
                  </div>
                </div>
                <div className="grn-action-required-card">
                  <label className="grn-form-field-label">Action Required</label>
                  <select
                    className="grn-input grn-input-select"
                    name="actionRequired"
                    value={formData.actionRequired}
                    onChange={handleInputChange}
                  >
                    <option value="">Select action...</option>
                    <option value="return">Return to Supplier</option>
                    <option value="replace">Request Replacement</option>
                    <option value="partial">Accept Partial Delivery</option>
                    <option value="investigate">Investigate Further</option>
                    <option value="waive">Waive Issue</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Document Uploads & Proof */}
          <div className="grn-section-wrapper">
            <div className="grn-section-header-with-bar">
              <h3 className="grn-section-title grn-section-title-orange">Document Uploads & Proof</h3>
            </div>
            <div className="grn-section grn-section-documents">
              <div className="grn-section-content">
                <div className="grn-document-grid">
                  <div className="grn-file-upload-card">
                    <label className="grn-file-upload-label">Proof of Delivery (POD) <span className="grn-required">*</span></label>
                    <input
                      type="file"
                      id="proofOfDelivery"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => handleFileUpload('proofOfDelivery', e.target.files[0])}
                      className="grn-file-input"
                    />
                    <label htmlFor="proofOfDelivery" className="grn-file-upload-button">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-upload-icon">
                        <path d="M12 3v12"></path>
                        <path d="m17 8-5-5-5 5"></path>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      </svg>
                      Upload
                    </label>
                    {documents.proofOfDelivery && (
                      <div className="grn-file-name">{documents.proofOfDelivery.name}</div>
                    )}
                  </div>
                  <div className="grn-file-upload-card">
                    <label className="grn-file-upload-label">Delivery Challan <span className="grn-required">*</span></label>
                    <input
                      type="file"
                      id="deliveryChallan"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => handleFileUpload('deliveryChallan', e.target.files[0])}
                      className="grn-file-input"
                    />
                    <label htmlFor="deliveryChallan" className="grn-file-upload-button">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-upload-icon">
                        <path d="M12 3v12"></path>
                        <path d="m17 8-5-5-5 5"></path>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      </svg>
                      Upload
                    </label>
                    {documents.deliveryChallan && (
                      <div className="grn-file-name">{documents.deliveryChallan.name}</div>
                    )}
                  </div>
                  <div className="grn-file-upload-card">
                    <label className="grn-file-upload-label">Packing List</label>
                    <input
                      type="file"
                      id="packingList"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => handleFileUpload('packingList', e.target.files[0])}
                      className="grn-file-input"
                    />
                    <label htmlFor="packingList" className="grn-file-upload-button">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-upload-icon">
                        <path d="M12 3v12"></path>
                        <path d="m17 8-5-5-5 5"></path>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      </svg>
                      Upload
                    </label>
                    {documents.packingList && (
                      <div className="grn-file-name">{documents.packingList.name}</div>
                    )}
                  </div>
                  <div className="grn-file-upload-card">
                    <label className="grn-file-upload-label">Damage Photos</label>
                    <input
                      type="file"
                      id="damagePhotos"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => handleFileUpload('damagePhotos', e.target.files[0])}
                      className="grn-file-input"
                    />
                    <label htmlFor="damagePhotos" className="grn-file-upload-button">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-upload-icon">
                        <path d="M12 3v12"></path>
                        <path d="m17 8-5-5-5 5"></path>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      </svg>
                      Upload
                    </label>
                    {documents.damagePhotos && (
                      <div className="grn-file-name">{documents.damagePhotos.name}</div>
                    )}
                  </div>
                  <div className="grn-file-upload-card">
                    <label className="grn-file-upload-label">Inspection Report</label>
                    <input
                      type="file"
                      id="inspectionReport"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => handleFileUpload('inspectionReport', e.target.files[0])}
                      className="grn-file-input"
                    />
                    <label htmlFor="inspectionReport" className="grn-file-upload-button">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-upload-icon">
                        <path d="M12 3v12"></path>
                        <path d="m17 8-5-5-5 5"></path>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      </svg>
                      Upload
                    </label>
                    {documents.inspectionReport && (
                      <div className="grn-file-name">{documents.inspectionReport.name}</div>
                    )}
                  </div>
                  <div className="grn-file-upload-card">
                    <label className="grn-file-upload-label">Other Documents</label>
                    <input
                      type="file"
                      id="otherDocuments"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => handleFileUpload('otherDocuments', e.target.files[0])}
                      className="grn-file-input"
                    />
                    <label htmlFor="otherDocuments" className="grn-file-upload-button">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-upload-icon">
                        <path d="M12 3v12"></path>
                        <path d="m17 8-5-5-5 5"></path>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      </svg>
                      Upload
                    </label>
                    {documents.otherDocuments && (
                      <div className="grn-file-name">{documents.otherDocuments.name}</div>
                    )}
                  </div>
                </div>
                <p className="grn-file-format-note">PDF, PNG, JPEG formats supported (Max 10MB per file)</p>
              </div>
            </div>
          </div>

          {/* Additional Notes / Remarks */}
          <div className="grn-section-wrapper">
            <div className="grn-section-header-with-bar">
              <h3 className="grn-section-title grn-section-title-gray">Additional Notes / Remarks</h3>
            </div>
            <div className="grn-section grn-section-notes">
              <div className="grn-form-field grn-form-field-full">
                <textarea
                  className="grn-textarea"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleInputChange}
                  placeholder="Include any additional observations, special storage requirements, handling instructions, or other relevant notes..."
                  rows="3"
                />
              </div>
            </div>
          </div>

          {/* Bottom Summary Bar */}
          <div className="grn-summary-bar">
            <div className="grn-summary-grid">
              <div className="grn-summary-item">
                <p className="grn-summary-label">Total Items</p>
                <p className="grn-summary-value">{totalItems}</p>
              </div>
              <div className="grn-summary-item">
                <p className="grn-summary-label">Total Qty Accepted</p>
                <p className="grn-summary-value grn-summary-accepted">{totalQtyAccepted}</p>
              </div>
              <div className="grn-summary-item">
                <p className="grn-summary-label">Total Qty Rejected</p>
                <p className="grn-summary-value grn-summary-rejected">{totalQtyRejected}</p>
              </div>
              <div className="grn-summary-item">
                <p className="grn-summary-label">Acceptance Rate</p>
                <p className="grn-summary-value grn-summary-rate">{acceptanceRate}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {isPreviewModalOpen && (
        <div className="grn-preview-modal-overlay" onClick={() => setIsPreviewModalOpen(false)}>
          <div 
            className="grn-preview-modal" 
            role="dialog"
            aria-modal="true"
            aria-labelledby="grn-preview-title"
            aria-describedby="grn-preview-description"
            onClick={(e) => e.stopPropagation()}
            tabIndex="-1"
          >
            <button 
              className="grn-preview-modal-close" 
              onClick={() => setIsPreviewModalOpen(false)}
              aria-label="Close preview"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>

            <div className="grn-preview-modal-header">
              <div>
                <h2 id="grn-preview-title" className="grn-preview-modal-title">GRN Preview - {formData.grnNumber}</h2>
                <p id="grn-preview-description" className="grn-preview-modal-subtitle">Review the Goods Received Note before submission. You can download or print this document.</p>
              </div>
            </div>

            <div className="grn-preview-content" id="grn-preview-content">
              {/* GRN Header */}
              <div className="grn-preview-header-section">
                <div className="grn-preview-header-top">
                  <div>
                    <h1 className="grn-preview-main-title">GOODS RECEIVED NOTE</h1>
                    <p className="grn-preview-doc-info">Document Type: GRN | Status: Draft</p>
                  </div>
                  <div className="grn-preview-header-right">
                    <p className="grn-preview-grn-number"><strong>GRN No:</strong> {formData.grnNumber}</p>
                    <p className="grn-preview-date">Date: {formData.grnDate}</p>
                    {!hideGrnPoInvoiceFields ? (
                      <p className="grn-preview-po">PO: {formData.selectedPO}</p>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Supplier & Delivery Info Grid */}
              <div className="grn-preview-info-grid">
                <div className="grn-preview-card grn-preview-card-orange">
                  <h3 className="grn-preview-card-title grn-preview-card-title-orange">Supplier Details</h3>
                  <div className="grn-preview-card-content">
                    <p><strong>Name:</strong> {formData.supplierName}</p>
                    <p><strong>GSTIN:</strong> {formData.supplierGSTIN}</p>
                    <p><strong>Address:</strong> {formData.address}</p>
                    <p><strong>Contact:</strong> {formData.contactPerson} ({formData.phone})</p>
                  </div>
                </div>
                <div className="grn-preview-card grn-preview-card-purple">
                  <h3 className="grn-preview-card-title grn-preview-card-title-purple">Delivery Information</h3>
                  <div className="grn-preview-card-content">
                    <p><strong>Delivery Challan:</strong> {formData.deliveryChallanNo}</p>
                    {!hideGrnPoInvoiceFields ? (
                      <p>
                        <strong>Invoice No:</strong> {formData.invoiceNumber}
                      </p>
                    ) : null}
                    <p><strong>Manifest Number:</strong> {formData.manifestNumber || 'N/A'}</p>
                    <p><strong>Delivery Date:</strong> {formData.deliveryDate}</p>
                    <p><strong>Vehicle:</strong> {formData.vehicleNumber} | Driver: {formData.driverName}</p>
                    <p><strong>Driver Contact:</strong> {formData.driverContact || 'N/A'}</p>
                    <p><strong>LR Number:</strong> {formData.lrNumber || 'N/A'}</p>
                    <p><strong>Transport:</strong> {formData.transportCompany}</p>
                    <p><strong>Received By:</strong> {formData.receivedBy || 'Not specified'}</p>
                    <p><strong>Warehouse:</strong> {formData.warehouseLocation}</p>
                  </div>
                </div>
              </div>

              {/* Received Items Table */}
              <div className="grn-preview-table-section">
                <div className="grn-preview-table-wrapper">
                  <table className="grn-preview-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>SKU</th>
                        <th>Description</th>
                        <th>HSN</th>
                        <th className="grn-text-center">UOM</th>
                        <th className="grn-text-right">Ordered</th>
                        <th className="grn-text-right">Received</th>
                        <th className="grn-text-right">Accepted</th>
                        <th className="grn-text-right">Rejected</th>
                        <th>Batch/Lot</th>
                        <th>Mfg Date</th>
                        <th>Exp Date</th>
                        <th>Condition</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>{item.sku || '-'}</td>
                          <td>{item.description || '-'}</td>
                          <td>{item.hsn || '-'}</td>
                          <td className="grn-text-center">{getUomLabel(item.uom) || '-'}</td>
                          <td className="grn-text-right">{item.qtyOrdered || '0'}</td>
                          <td className="grn-text-right">{item.qtyReceived || '0'}</td>
                          <td className="grn-text-right grn-text-green">{item.qtyAccepted || '0'}</td>
                          <td className="grn-text-right grn-text-red">{item.qtyRejected || '0'}</td>
                          <td>{item.batchLot || '-'}</td>
                          <td>{item.mfgDate || '-'}</td>
                          <td>{item.expDate || '-'}</td>
                          <td>
                            <span className={`grn-preview-badge grn-preview-badge-${item.condition?.toLowerCase() || 'good'}`}>
                              {item.condition || 'Good'}
                            </span>
                          </td>
                          <td className="grn-text-small">{item.remarks || ''}</td>
                        </tr>
                      ))}
                      <tr className="grn-preview-table-total">
                      <td colSpan="1"></td>
                        <td className="grn-text-right"><strong>Totals:</strong></td>
                        <td colSpan="3"></td>
                        <td className="grn-text-right"><strong>{totalQtyOrdered}</strong></td>
                        <td className="grn-text-right"><strong>{totalQtyReceived}</strong></td>
                        <td className="grn-text-right grn-text-green"><strong>{totalQtyAccepted}</strong></td>
                        <td className="grn-text-right grn-text-red"><strong>{totalQtyRejected}</strong></td>
                        <td colSpan="5"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Physical Verification Summary */}
              <div className="grn-preview-card grn-preview-card-indigo">
                <h3 className="grn-preview-card-title grn-preview-card-title-indigo">Physical Verification Summary</h3>
                <div className="grn-preview-verification-grid">
                  <div>
                    <p className="grn-preview-label">Packages Expected</p>
                    <p className="grn-preview-value">{formData.packagesExpected || '0'}</p>
                  </div>
                  <div>
                    <p className="grn-preview-label">Packages Received</p>
                    <p className="grn-preview-value">{formData.packagesReceived || '0'}</p>
                  </div>
                  <div>
                    <p className="grn-preview-label">Weight Expected (kg)</p>
                    <p className="grn-preview-value">{formData.weightExpected || '0'}</p>
                  </div>
                  <div>
                    <p className="grn-preview-label">Weight Received (kg)</p>
                    <p className="grn-preview-value">{formData.weightReceived || '0'}</p>
                  </div>
                </div>
                <div className="grn-preview-verification-checks">
                  <p className={formData.dimensionsVerified ? 'grn-check-verified' : 'grn-check-unverified'}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="m9 12 2 2 4-4"></path>
                    </svg>
                    Dimensions Verified
                  </p>
                  <p className={formData.packagingIntegrityOK ? 'grn-check-verified' : 'grn-check-unverified'}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="m9 12 2 2 4-4"></path>
                    </svg>
                    Packaging Integrity
                  </p>
                  <p className={formData.sealsVerified ? 'grn-check-verified' : 'grn-check-unverified'}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="m9 12 2 2 4-4"></path>
                    </svg>
                    Seals Verified
                  </p>
                  {formData.sealsVerified && formData.sealNumbers && (
                    <p className="grn-preview-seal-numbers">Seal Nos: {formData.sealNumbers}</p>
                  )}
                </div>
              </div>

              {/* Quality Inspection & Discrepancies Grid */}
              <div className="grn-preview-info-grid">
                <div className="grn-preview-card grn-preview-card-green">
                  <h3 className="grn-preview-card-title grn-preview-card-title-green">Quality Inspection</h3>
                  <div className="grn-preview-card-content">
                    {formData.packagingIntact && (
                      <p className="grn-check-verified">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="m9 12 2 2 4-4"></path>
                        </svg>
                         Packaging Intact
                      </p>
                    )}
                    {formData.correctLabeling && (
                      <p className="grn-check-verified">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="m9 12 2 2 4-4"></path>
                        </svg>
                         Correct Labeling
                      </p>
                    )}
                    {formData.noVisibleDamage && (
                      <p className="grn-check-verified">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="m9 12 2 2 4-4"></path>
                        </svg>
                         No Visible Damage
                      </p>
                    )}
                    {formData.quantityMatches && (
                      <p className="grn-check-verified">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="m9 12 2 2 4-4"></path>
                        </svg>
                         Quantity Matches
                      </p>
                    )}
                    {formData.meetsQualityStandards && (
                      <p className="grn-check-verified">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="m9 12 2 2 4-4"></path>
                        </svg>
                         Quality Standards Met
                      </p>
                    )}
                    {formData.allDocumentsComplete && (
                      <p className="grn-check-verified">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="m9 12 2 2 4-4"></path>
                        </svg>
                         Documents Complete
                      </p>
                    )}
                    {!formData.packagingIntact && !formData.correctLabeling && !formData.noVisibleDamage && 
                     !formData.quantityMatches && !formData.meetsQualityStandards && !formData.allDocumentsComplete && (
                      <p className="grn-check-unverified">No quality inspection items selected</p>
                    )}
                  </div>
                  <div className="grn-preview-inspector-info">
                    <p><strong>Inspector:</strong> {formData.inspectorName || 'Not specified'}</p>
                    <p><strong>Date:</strong> {formData.inspectionDate}</p>
                    {formData.defectDescription && (
                      <p><strong>Defect / Issue Description:</strong> {formData.defectDescription}</p>
                    )}
                  </div>
                </div>
                <div className="grn-preview-card grn-preview-card-red">
                  <h3 className="grn-preview-card-title grn-preview-card-title-red">Discrepancies & Issues</h3>
                  <div className="grn-preview-discrepancies-content">
                    {(formData.shortageDetected || formData.damageDetected || formData.qualityIssue) ? (
                      <div>
                        {formData.shortageDetected && formData.shortageDescription && (
                          <div className="grn-preview-discrepancy-item">
                            <p><strong>Shortage Detected:</strong> {formData.shortageDescription}</p>
                          </div>
                        )}
                        {formData.damageDetected && formData.damageDescription && (
                          <div className="grn-preview-discrepancy-item">
                            <p><strong>Damage Detected:</strong> {formData.damageDescription}</p>
                          </div>
                        )}
                        {formData.qualityIssue && formData.qualityIssueDescription && (
                          <div className="grn-preview-discrepancy-item">
                            <p><strong>Quality Issue:</strong> {formData.qualityIssueDescription}</p>
                          </div>
                        )}
                        {formData.actionRequired && (
                          <div className="grn-preview-discrepancy-item">
                            <p><strong>Action Required:</strong> {formData.actionRequired}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="grn-preview-no-discrepancies">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon-large">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="m9 12 2 2 4-4"></path>
                        </svg>
                        <p className="grn-preview-no-discrepancies-text">No discrepancies reported</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Notes / Remarks */}
              {formData.additionalNotes && (
                <div className="grn-preview-card grn-preview-card-gray">
                  <h3 className="grn-preview-card-title grn-preview-card-title-gray">Additional Notes / Remarks</h3>
                  <div className="grn-preview-card-content">
                    <p>{formData.additionalNotes}</p>
                  </div>
                </div>
              )}

              {/* Summary Stats */}
              <div className="grn-preview-summary-stats">
                <div className="grn-preview-summary-item grn-preview-summary-blue">
                  <p className="grn-preview-summary-label">Total Items</p>
                  <p className="grn-preview-summary-value">{totalItems}</p>
                </div>
                <div className="grn-preview-summary-item grn-preview-summary-green">
                  <p className="grn-preview-summary-label">Accepted</p>
                  <p className="grn-preview-summary-value">{totalQtyAccepted}</p>
                </div>
                <div className="grn-preview-summary-item grn-preview-summary-red">
                  <p className="grn-preview-summary-label">Rejected</p>
                  <p className="grn-preview-summary-value">{totalQtyRejected}</p>
                </div>
                <div className="grn-preview-summary-item grn-preview-summary-orange">
                  <p className="grn-preview-summary-label">Acceptance Rate</p>
                  <p className="grn-preview-summary-value">{acceptanceRate}%</p>
                </div>
              </div>

              {/* Signatures */}
              <div className="grn-preview-signatures">
                <div className="grn-preview-signature-item">
                  <div className="grn-preview-signature-line"></div>
                  <p className="grn-preview-signature-label">Prepared By</p>
                  <p className="grn-preview-signature-placeholder">_______________</p>
                </div>
                <div className="grn-preview-signature-item">
                  <div className="grn-preview-signature-line"></div>
                  <p className="grn-preview-signature-label">Inspected By</p>
                  <p className="grn-preview-signature-placeholder">_______________</p>
                </div>
                <div className="grn-preview-signature-item">
                  <div className="grn-preview-signature-line"></div>
                  <p className="grn-preview-signature-label">Approved By</p>
                  <p className="grn-preview-signature-name">Store Manager</p>
                </div>
              </div>
            </div>

            <div className="grn-preview-modal-footer">
              <button className="grn-btn grn-btn-secondary" onClick={() => setIsPreviewModalOpen(false)}>
                Close
              </button>
              <button 
                className="grn-btn grn-btn-primary" 
                onClick={handleSubmitGRN}
                disabled={saving || submitting}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
                {submitting ? 'Submitting...' : 'Submit GRN'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

